import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Box, Stack, AppBar, Button, Toolbar, IconButton } from '@mui/material';

import { paths } from 'src/routes/paths';

import logo from 'src/assets/logo/logo.png';
import { COLORS } from 'src/online-payment/constants';
import { useFilteredNavItems } from 'src/online-payment/hooks';

import Iconify from 'src/components/iconify';

import DrawerMenu from './Drawer';

/* =================== NAVBAR =================== */
const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const filteredNavItems = useFilteredNavItems();

  const isActive = (path: string) =>
    location.pathname === path ||
    (path === paths.pointPay.dashboard && location.pathname === paths.pointPay.root);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleNavigation = (path: string) => {
    // CLOSE DRAWER IMMEDIATELY BEFORE NAVIGATION
    setMobileOpen(false);
    // NAVIGATE AFTER A SHORT DELAY TO ENSURE DRAWER CLOSES
    setTimeout(() => {
      navigate(path);
    }, 100);
  };

  // CLOSE DRAWER WHEN ROUTE CHANGES (SAFETY FALLBACK)
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: COLORS.cardBg,
          borderBottom: '1px solid',
          borderColor: COLORS.border,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {/* LOGO */}
          <Box
            component="img"
            src={logo}
            alt="PointCraft"
            onClick={() => navigate(paths.pointPay.dashboard)}
            sx={{
              height: { xs: 28, sm: 36 },
              width: 'auto',
              cursor: 'pointer',
            }}
          />

          {/* MOBILE HAMBURGER MENU */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{
              ml: 'auto',
              display: { md: 'none' },
              color: COLORS.textPrimary,
            }}
          >
            <Iconify icon="lucide:menu" width={24} />
          </IconButton>

          {/* DESKTOP NAVIGATION TABS */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ ml: 'auto', display: { xs: 'none', md: 'flex' } }}
          >
            {filteredNavItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  startIcon={<Iconify icon={item.icon} width={20} />}
                  sx={{
                    px: 3,
                    py: 1.25,
                    color: active ? COLORS.cardBg : COLORS.textSecondary,
                    bgcolor: active ? '#000' : 'transparent',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    boxShadow: active ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
                    '&:hover': {
                      bgcolor: active ? '#1a1a1a' : 'rgba(0, 0, 0, 0.04)',
                      boxShadow: active ? '0 6px 16px rgba(0, 0, 0, 0.25)' : 'none',
                    },
                  }}
                >
                  {item.title}
                </Button>
              );
            })}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* MOBILE DRAWER */}
      <DrawerMenu
        navItems={filteredNavItems}
        isActive={isActive}
        handleNavigation={handleNavigation}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
    </>
  );
};

export default Navbar;
